import React, { useCallback, useMemo, useRef, useState } from 'react';
import type { GridCell, GridRow, Milestone } from '../../types/life';
import { cn } from '../../utils/cn';
import { formatCountdown, formatNumber } from '../../utils/format';
import { SLICES_PER_YEAR, formatDayRange } from '../../utils/lifeGrid';
import type { RemainingCounter } from '../../utils/counters';
import { useElementSize } from '../../hooks/useElementSize';
import { RemainingCounters } from '../RemainingCounters';
export type LifetimeStyle = 'faded' | 'struck';
interface HoverInfo {
  cell: GridCell;
  age: number;
  x: number;
  y: number;
}
interface LifetimeViewProps {
  rows: GridRow[];
  now: Date;
  style: LifetimeStyle;
  compact: boolean;
  counters: RemainingCounter[];
  milestoneByCell: Map<string, Milestone>;
  focusedKey: string | null;
  onSelectCell: (cell: GridCell) => void;
}
const GROUP_SIZE = 4;
/** С этой доли горизонта точки начинают растворяться: до них можно не дойти. */
const FADE_FROM = 0.85;
const FADE_DEPTH = 0.78;
const GROUPS = SLICES_PER_YEAR / GROUP_SIZE;
const GROUP_GAP_RATIO = 0.7;
const DECADE_GAP_RATIO = 0.9;
interface Metrics {
  pitchY: number;
  pitchX: number;
  groupGap: number;
  dot: number;
  gridWidth: number;
  labelWidth: number;
  yearWidth: number;
}
function measure(rowsCount: number, width: number, height: number, compact: boolean): Metrics {
  const labelWidth = compact ? 22 : 28;
  // на узком экране календарный год не влезает четырьмя цифрами — оставляем только возраст
  const yearWidth = compact ? 0 : 38;
  const decadeGaps = Math.max(0, Math.floor((rowsCount - 1) / 10));
  const pitchY = Math.min(14, (height - 2) / (rowsCount + decadeGaps * DECADE_GAP_RATIO));
  const available = width - labelWidth - yearWidth;
  const units = SLICES_PER_YEAR + (GROUPS - 1) * GROUP_GAP_RATIO;
  const pitchX = Math.max(2.5, Math.min(available / units, pitchY * 1.6));
  const groupGap = pitchX * GROUP_GAP_RATIO;
  return {
    pitchY,
    pitchX,
    groupGap,
    dot: Math.max(2, Math.min(8, Math.round(Math.min(pitchY, pitchX) * 0.62))),
    gridWidth: SLICES_PER_YEAR * pitchX + (GROUPS - 1) * groupGap,
    labelWidth,
    yearWidth
  };
}
interface DotFieldProps {
  rows: GridRow[];
  style: LifetimeStyle;
  milestoneByCell: Map<string, Milestone>;
  focusedKey: string | null;
  metrics: Metrics;
  onPointer: (row: GridRow, event: React.MouseEvent<HTMLButtonElement>) => void;
  onActivate: (row: GridRow, event: React.MouseEvent<HTMLButtonElement>) => void;
}
const DotField = React.memo(function DotField({
  rows,
  style,
  milestoneByCell,
  focusedKey,
  metrics,
  onPointer,
  onActivate
}: DotFieldProps) {
  const {
    pitchY,
    pitchX,
    groupGap,
    dot,
    gridWidth,
    labelWidth,
    yearWidth
  } = metrics;
  const struck = style === 'struck';
  const pastSize = struck ? dot : Math.max(2, dot - 1);
  const futureSize = struck ? Math.max(2, dot - 1) : dot;
  const lineHeight = Math.max(1, Math.round(pitchY * 0.12));
  const pastLabel = struck ? 'text-ink line-through decoration-1' : 'text-muted line-through decoration-1';
  const futureLabel = struck ? 'text-muted' : 'text-ink';
  return <div data-id="element-149" data-mp="10fbh0btgrdi4">
      {rows.map((row, index) => {
      const age = Number(row.label);
      const decadeMark = age % 10 === 0;
      const currentIndex = row.cells.findIndex(cell => cell.state === 'current');
      const isCurrent = currentIndex >= 0;
      const fullyLived = row.fraction >= 1;
      const showLabel = decadeMark || isCurrent;

      // точный x конца прожитого — с учётом разрывов между четвёрками
      const strikeTo = (() => {
        if (row.fraction <= 0) return 0;
        if (fullyLived) return gridWidth;
        const exact = row.fraction * SLICES_PER_YEAR;
        const whole = Math.floor(exact);
        return whole * pitchX + Math.floor(whole / GROUP_SIZE) * groupGap + (exact - whole) * pitchX;
      })();
      const share = age / rows.length;
      const opacity = share > FADE_FROM ? Math.max(0.2, 1 - (share - FADE_FROM) / (1 - FADE_FROM) * FADE_DEPTH) : 1;
      return <div key={row.key} className="flex items-center" style={{
        height: pitchY,
        marginTop: decadeMark && index > 0 ? pitchY * DECADE_GAP_RATIO : 0,
        opacity
      }} data-id="element-150" data-mp="2ya8k7bnb9lhv" data-mpi={index}>
            <div className={cn('shrink-0 pr-2 text-right font-mono text-[10px] leading-none tabular-nums', isCurrent ? 'text-accent' : fullyLived ? pastLabel : futureLabel)} style={{
          width: labelWidth
        }} data-id="element-151" data-mp="3c4ro5riwkx2a">
              {showLabel ? age : ''}
            </div>

            <button type="button" onMouseMove={event => onPointer(row, event)} onClick={event => onActivate(row, event)} aria-label={`Год жизни ${age}: прожито ${Math.round(row.fraction * SLICES_PER_YEAR)} из ${SLICES_PER_YEAR} недель`} className="relative flex items-center focus-visible:outline focus-visible:outline-1 focus-visible:outline-accent" style={{
          width: gridWidth,
          height: pitchY
        }} data-id="element-152" data-mp="3v2lwe3c7j4jm">
              {struck && strikeTo > 0 && <span aria-hidden className="bg-ink absolute left-0 top-1/2 -translate-y-1/2" style={{
            width: strikeTo,
            height: lineHeight
          }} data-id="element-153" data-mp="1jgaa44l62bj5" />}

              {Array.from({
            length: GROUPS
          }, (_, groupIndex) => <span key={groupIndex} className="flex items-center" style={{
            marginLeft: groupIndex > 0 ? groupGap : 0
          }} data-id="element-154" data-mp="275uvsv8pfmw4">
                  {row.cells.slice(groupIndex * GROUP_SIZE, groupIndex * GROUP_SIZE + GROUP_SIZE).map(cell => {
              const milestone = milestoneByCell.get(cell.key);
              const focused = focusedKey === cell.key;
              const accent = Boolean(milestone) || focused || cell.state === 'current';
              const past = cell.state === 'past';
              const size = accent ? dot + 2 : past ? pastSize : futureSize;
              return <span key={cell.key} data-cell={cell.key} className="flex shrink-0 items-center justify-center" style={{
                width: pitchX,
                height: pitchY
              }} data-id="element-155" data-mp="j727e3rfntjm">
                          <span aria-hidden className={cn('rounded-full', accent ? 'bg-accent' : past ? struck ? 'bg-ink' : 'bg-faint' : struck ? 'bg-faint' : 'bg-ink')} style={{
                  width: size,
                  height: size,
                  boxShadow: accent ? '0 0 0 2px var(--paper)' : undefined
                }} data-id="element-156" data-mp="a873iybv2vjh" />
                        </span>;
            })}
                </span>)}
            </button>

            {yearWidth > 0 && <div className={cn('shrink-0 pl-2 font-mono text-[10px] leading-none tabular-nums', isCurrent ? 'text-accent' : fullyLived ? pastLabel : futureLabel)} style={{
          width: yearWidth
        }} data-id="element-157" data-mp="3vr67x2g8n9g6">
                {showLabel ? row.subLabel : ''}
              </div>}
          </div>;
    })}
    </div>;
});
export function LifetimeView({
  rows,
  now,
  style,
  compact,
  counters,
  milestoneByCell,
  focusedKey,
  onSelectCell
}: LifetimeViewProps) {
  const [ref, size] = useElementSize<HTMLDivElement>();
  const [hover, setHover] = useState<HoverInfo | null>(null);
  const hoverKey = useRef<string | null>(null);
  const metrics = useMemo(() => size.width > 0 && size.height > 0 ? measure(rows.length, size.width, size.height, compact) : null, [rows.length, size.width, size.height, compact]);
  const cellFromEvent = (row: GridRow, event: React.MouseEvent<HTMLButtonElement>, m: Metrics) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const groupSpan = GROUP_SIZE * m.pitchX + m.groupGap;
    const group = Math.min(GROUPS - 1, Math.max(0, Math.floor(x / groupSpan)));
    const withinGroup = Math.min(GROUP_SIZE - 1, Math.max(0, Math.floor((x - group * groupSpan) / m.pitchX)));
    const index = Math.min(SLICES_PER_YEAR - 1, group * GROUP_SIZE + withinGroup);
    return {
      cell: row.cells[index],
      x: rect.left + index * m.pitchX + group * m.groupGap + m.pitchX / 2,
      y: rect.top
    };
  };
  const handlePointer = useCallback((row: GridRow, event: React.MouseEvent<HTMLButtonElement>) => {
    if (!metrics) return;
    const {
      cell,
      x,
      y
    } = cellFromEvent(row, event, metrics);
    if (hoverKey.current === cell.key) return;
    hoverKey.current = cell.key;
    setHover({
      cell,
      age: Number(row.label),
      x,
      y
    });
  }, [metrics]);
  const handleActivate = useCallback((row: GridRow, event: React.MouseEvent<HTMLButtonElement>) => {
    if (!metrics) return;
    onSelectCell(cellFromEvent(row, event, metrics).cell);
  }, [metrics, onSelectCell]);
  const clearHover = () => {
    hoverKey.current = null;
    setHover(null);
  };
  const total = rows.length * SLICES_PER_YEAR;
  const lived = Math.round(rows.reduce((acc, row) => acc + row.fraction, 0) * SLICES_PER_YEAR);
  const blockWidth = metrics ? metrics.labelWidth + metrics.gridWidth + metrics.yearWidth : undefined;
  const hoverMilestone = hover ? milestoneByCell.get(hover.cell.key) : undefined;
  const hoverStatus = hover ? hover.cell.state === 'past' ? style === 'struck' ? 'зачёркнута' : 'прожита' : hover.cell.state === 'current' ? 'идёт прямо сейчас' : `через ${formatCountdown(hover.cell.start, now)}` : '';
  const tooltipX = hover ? Math.min(Math.max(hover.x, 120), (typeof window !== 'undefined' ? window.innerWidth : 1200) - 120) : 0;
  return <div className="flex h-full flex-col items-center" data-id="element-158" data-mp="3gmyr908f18vx">
      <div ref={ref} className="flex min-h-0 w-full flex-1 items-start justify-center overflow-hidden" onMouseLeave={clearHover} data-id="element-159" data-mp="3cjai4d44y58k">
        {metrics && <DotField rows={rows} style={style} milestoneByCell={milestoneByCell} focusedKey={focusedKey} metrics={metrics} onPointer={handlePointer} onActivate={handleActivate} data-id="element-160" data-mp="rtl422b9angx" />}
      </div>

      <div className="border-line mt-4 flex shrink-0 flex-wrap items-baseline justify-between gap-x-8 gap-y-2 border-t pt-3" style={{
      width: blockWidth
    }} data-id="element-161" data-mp="pexsyd4xqev7">
        <RemainingCounters items={counters} data-id="element-162" data-mp="ug3nmgi5oc7d" />
        <span className="text-muted flex items-center gap-4 text-[11px] sm:gap-6" data-id="element-163" data-mp="1i323pd2d5i4r">
          <span className="flex items-center gap-2" data-id="element-164" data-mp="1n2ewsfjtvlfs">
            <span className={cn('inline-block h-1.5 w-1.5 rounded-full', style === 'struck' ? 'bg-ink' : 'bg-faint')} aria-hidden data-id="element-165" data-mp="1n253ahzkqw5c" />
            прожито <span className="text-ink font-mono tabular-nums" data-id="element-166" data-mp="1dbkbbdeeopve">{formatNumber(lived)}</span>
          </span>
          <span className="flex items-center gap-2" data-id="element-167" data-mp="211o40pq2357m">
            <span className={cn('inline-block h-1.5 w-1.5 rounded-full', style === 'struck' ? 'bg-faint' : 'bg-ink')} aria-hidden data-id="element-168" data-mp="25swcnjjkamjq" />
            осталось <span className="text-accent font-mono tabular-nums" data-id="element-169" data-mp="2fmsflksi0xp4">{formatNumber(total - lived)}</span>
          </span>
        </span>
      </div>

      {hover && <div role="tooltip" className="pointer-events-none fixed z-40 -translate-x-1/2 -translate-y-full" style={{
      left: tooltipX,
      top: Math.max(hover.y - 10, 84)
    }} data-id="element-170" data-mp="26n5hb7v2g80a">
          <div className="bg-ink text-paper px-2.5 py-2 font-mono text-[11px] leading-snug shadow-xl" data-id="element-171" data-mp="1wrvb07xmxegp">
            <p className="font-medium" data-id="element-172" data-mp="1ry98h8l6qqym">{formatDayRange(hover.cell.start, hover.cell.end)}</p>
            <p style={{
          opacity: 0.6
        }} data-id="element-173" data-mp="1n10msy0tw4sm">
              возраст {hover.age} · неделя {hover.cell.label}
            </p>
            <p style={{
          opacity: 0.6
        }} data-id="element-174" data-mp="1iwsz9463wuvz">{hoverStatus}</p>
            {hoverMilestone && <p className="text-accent-soft mt-1 font-medium" data-id="element-175" data-mp="1dz9l049esi41">{hoverMilestone.title}</p>}
          </div>
        </div>}
    </div>;
}